import { Clock } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const QUARTERS = ["00", "15", "30", "45"];

/**
 * TimeSlotPicker — HH + 15-min slot picker.
 *
 * Native `<input type="time">` allows every single minute which is way
 * too granular for CRM scheduling. This wraps two shadcn Selects so the
 * hour column shows 00–23 and the minute column snaps to 00/15/30/45.
 *
 * Value is a `"HH:MM"` string (empty when either side is unset). The
 * picker never emits an invalid partial value — if the caller only
 * chose the hour, we default the minute to "00".
 */
export default function TimeSlotPicker({
  value,
  onChange,
  testId,
  className = "",
}) {
  const [hh = "", mm = ""] = (value || "").split(":");
  // Snap incoming values that aren't on the 15-min grid to the nearest slot.
  // Prevents surprises when the row was created before the picker existed.
  const snappedMm = QUARTERS.includes(mm)
    ? mm
    : mm
      ? String(Math.min(45, Math.round(parseInt(mm, 10) / 15) * 15)).padStart(2, "0")
      : "";

  const emit = (nextHh, nextMm) => {
    if (!nextHh) {
      onChange("");
      return;
    }
    onChange(`${nextHh}:${nextMm || "00"}`);
  };

  return (
    <div className={`flex items-center gap-1.5 ${className}`}>
      <Clock className="h-3.5 w-3.5 shrink-0 text-zinc-400" />
      <Select value={hh} onValueChange={(v) => emit(v, snappedMm)}>
        <SelectTrigger
          className="h-9 w-[68px] px-2 text-sm tabular-nums"
          data-testid={testId ? `${testId}-hour` : undefined}
        >
          <SelectValue placeholder="—" />
        </SelectTrigger>
        <SelectContent className="max-h-[240px]">
          {HOURS.map((h) => (
            <SelectItem key={h} value={h} className="tabular-nums">
              {h}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <span className="font-semibold text-zinc-400">:</span>
      <Select value={snappedMm} onValueChange={(v) => emit(hh, v)}>
        <SelectTrigger
          className="h-9 w-[68px] px-2 text-sm tabular-nums"
          data-testid={testId ? `${testId}-minute` : undefined}
        >
          <SelectValue placeholder="—" />
        </SelectTrigger>
        <SelectContent>
          {QUARTERS.map((m) => (
            <SelectItem key={m} value={m} className="tabular-nums">
              {m}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
